import { useQuery, type OperationVariables, type QueryHookOptions } from '@apollo/client'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { useCallback, useMemo, useState } from 'react'

export const useConnectionQuery = <
  TKey extends keyof TData & string,
  TData extends Record<
    TKey,
    {
      pageInfo: {
        hasNextPage: boolean
        endCursor?: string
      }
      nodes: any[]
    }
  >,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables>,
) => {
  const { data, loading, error, fetchMore } = useQuery<TData, TVariables>(query, options)
  const [fetchingMore, setFetchingMore] = useState(false)

  const key = useMemo(() => {
    if (!data) return '' as TKey
    const keys = Object.keys(data) as TKey[]
    const collectionKey = keys.find((k) => {
      const v = data[k]
      return 'pageInfo' in v && Array.isArray(v.nodes)
    })
    return (collectionKey ?? '') as TKey
  }, [data])
  const pageInfo = useMemo(() => data?.[key]?.pageInfo, [data, key])
  const loadingOrFetching = useMemo(() => loading || fetchingMore, [loading, fetchingMore])

  const loadMore = useCallback(() => {
    const edges = data?.[key]?.nodes
    if (!!edges && pageInfo?.hasNextPage == true) {
      setFetchingMore(true)
      fetchMore({
        variables: {
          first: options.variables?.first,
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const nextConn = fetchMoreResult[key]
          if (!nextConn?.nodes?.length) return prev

          const prevConn = prev[key]

          return {
            ...prev,
            [key]: {
              ...nextConn,
              nodes: [...prevConn.nodes, ...nextConn.nodes],
            },
          }
        },
      }).finally(() => {
        setFetchingMore(false)
      })
    }
  }, [data, key, pageInfo, fetchMore, options.variables?.first])

  return {
    data,
    loading: loadingOrFetching,
    hasNextPage: pageInfo?.hasNextPage,
    error,
    loadMore,
  }
}
